import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import { InstantFieldComponent, SelectFieldComponent, SelectFieldOption } from '@evorait/pace-components';
import { TimesheetFormEntry, TimesheetManualRow } from '@evorait/pace-core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { formatRowDuration } from './row-duration';

/**
 * An entry the technician added by hand to account for time no confirmation covers.
 *
 * Unlike a work row this is theirs to edit outright: times, what the time was spent on, and where.
 * A travel-back entry is the exception — its kind is fixed, so the task-type dropdown gives way to
 * a plain label.
 */
@Component({
    selector: 'pace-timesheet-manual-row',
    standalone: true,
    imports: [FaIconComponent, FormField, InstantFieldComponent, SelectFieldComponent, TranslateModule],
    templateUrl: './timesheet-manual-row.component.html',
    styleUrl: './timesheet-row.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimesheetManualRowComponent {
    /** the projected row, for everything the form does not edit */
    public readonly row = input.required<TimesheetManualRow>();

    /** the entry's field, whose start, end, task type and address the inputs bind to */
    public readonly entry = input.required<FieldTree<TimesheetFormEntry>>();

    /** the task types this entry may be given, from the value-help slice */
    public readonly taskTypeOptions = input<readonly SelectFieldOption[]>([]);
    /** for manual timesheet rows, only use the numeric CODNR values from the WorkCode ValueHelperSet */
    public readonly taskTypeOptionsFiltered = computed(() =>
        this.taskTypeOptions().filter((vh) => vh.value.match(/^[0-9]*$/)),
    );

    /** whether the config displays addresses on the timesheet at all */
    public readonly displayAddress = input(false);

    /** whether the entry may be editable, rather than only shown */
    public readonly editable = input(false);

    /**
     * ISO instants the native pickers are limited to; empty for no limit. Taken from the row's
     * neighbour-derived bounds, so an entry cannot be dragged past the ones around it.
     */
    public readonly minInstant = input('');
    public readonly maxInstant = input('');

    /** the user asked to pick an address for this entry */
    public readonly selectAddress = output<void>();

    /** the user asked to remove this entry, reopening the gap it filled */
    public readonly deleteEntry = output<void>();

    /**
     * What the address button shows: the chosen place, or '' while none resolves.
     *
     * The row knows only the entry's address id (`row().address`), not the address slice, so the
     * page resolves the label and hands it over — the row stays dumb.
     */
    public readonly addressLabel = input('');

    protected readonly faTrashAlt = faTrashAlt;

    /**
     * The span the fields currently hold, as `HH:mm`, for the duration column.
     *
     * Read from the fields rather than from the projected row so it follows what has been typed. The
     * row comes from the store, which does not take an edit that would put the timesheet out of
     * order — and a duration still describing the old times, sitting next to the value that was
     * refused, reads as though the input had not registered.
     */
    protected readonly duration = computed(() =>
        formatRowDuration(this.entry().start().value(), this.entry().end().value()),
    );

    /** A travel-back entry's kind is fixed by the entry it follows, so it is not chosen. */
    protected readonly isTravel = computed(() => this.row().travelType !== undefined);

    /** An address that cannot be sent yet, which the button flags rather than hides. */
    protected readonly addressMissing = computed(() => !this.addressLabel());
}
